import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Phone, MapPin, CalendarCheck } from "lucide-react";
import { APPOINTMENT_SERVICES, TIME_SLOTS, CLINIC } from "../../data/content";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const inputCls =
    "w-full rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm font-semibold text-ink placeholder:font-medium placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-teal-600 focus:ring-4 focus:ring-teal-600/15";

const Field = ({ label, required, children, testid }) => (
    <label className="block" data-testid={testid}>
        <span className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.15em] text-ink/70">
            {label} {required && <span className="text-teal-accent">*</span>}
        </span>
        {children}
    </label>
);

export const Appointment = () => {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        service: "",
        date: "",
        time: "",
        concern: "",
    });

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        toast.success("Your details are ready.", {
            description: "WhatsApp booking will be connected in the next update.",
        });
    };

    const today = new Date().toISOString().split("T")[0];

    return (
        <section id="appointment" data-testid="appointment-section" className="py-24 lg:py-32">
            <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-10">
                <div>
                    <SectionHeading
                        number="08"
                        eyebrow="Appointments"
                        title="Book Your Dental"
                        accent="Appointment"
                        sub="Fill in your details and preferred appointment time. Your booking request will open in WhatsApp for confirmation."
                    />
                    <Reveal delay={0.2}>
                        <div className="mt-10 space-y-4 rounded-3xl bg-ink p-8 text-[#f8f6f0]">
                            <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-teal-300">
                                <CalendarCheck className="h-4 w-4" /> Visit the Clinic
                            </p>
                            <p data-testid="appointment-clinic-address" className="font-display text-xl leading-relaxed">
                                {CLINIC.addressLines.join(" ")}
                            </p>
                            <div className="flex flex-wrap gap-3 pt-2">
                                <a
                                    data-testid="appointment-call-btn"
                                    href={`tel:${CLINIC.phoneTel}`}
                                    className="inline-flex items-center gap-2 rounded-full bg-teal-accent px-5 py-3 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal-500"
                                >
                                    <Phone className="h-4 w-4" /> {CLINIC.phoneDisplay}
                                </a>
                                <a
                                    data-testid="appointment-directions-btn"
                                    href={CLINIC.mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-bold transition-[background-color,border-color] duration-300 hover:border-teal-300 hover:bg-white/10"
                                >
                                    <MapPin className="h-4 w-4 text-teal-300" /> Get Directions
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <form
                        data-testid="appointment-form"
                        onSubmit={onSubmit}
                        className="rounded-3xl border border-ink/10 bg-white p-7 shadow-[0_30px_60px_-30px_rgba(11,31,42,0.25)] sm:p-9"
                    >
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <Field label="Full Name" required testid="field-full-name">
                                <input
                                    data-testid="input-full-name"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your full name"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Phone Number" required testid="field-phone">
                                <input
                                    data-testid="input-phone"
                                    required
                                    type="tel"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="Your phone number"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Email Address" testid="field-email">
                                <input
                                    data-testid="input-email"
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@example.com"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Select Service" required testid="field-service">
                                <select
                                    data-testid="select-service"
                                    required
                                    value={form.service}
                                    onChange={set("service")}
                                    className={inputCls}
                                >
                                    <option value="" disabled>
                                        Choose a service
                                    </option>
                                    {APPOINTMENT_SERVICES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Preferred Date" required testid="field-date">
                                <input
                                    data-testid="input-date"
                                    required
                                    type="date"
                                    min={today}
                                    value={form.date}
                                    onChange={set("date")}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Preferred Time" required testid="field-time">
                                <select
                                    data-testid="select-time"
                                    required
                                    value={form.time}
                                    onChange={set("time")}
                                    className={inputCls}
                                >
                                    <option value="" disabled>
                                        Choose a time
                                    </option>
                                    {TIME_SLOTS.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Dental Concern" testid="field-concern">
                                    <textarea
                                        data-testid="input-concern"
                                        rows={4}
                                        value={form.concern}
                                        onChange={set("concern")}
                                        placeholder="Briefly describe your dental concern (optional)"
                                        className={`${inputCls} resize-none`}
                                    />
                                </Field>
                            </div>
                        </div>
                        <button
                            data-testid="appointment-submit-btn"
                            type="submit"
                            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-teal-accent px-8 py-4 text-sm font-extrabold uppercase tracking-[0.15em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal-700"
                        >
                            <MessageCircle className="h-5 w-5" />
                            Book Appointment on WhatsApp
                        </button>
                        <p data-testid="appointment-whatsapp-note" className="mt-4 text-center text-xs font-semibold leading-relaxed text-slate-500">
                            Your appointment details will open in WhatsApp. You can review the message and manually
                            send it to the clinic.
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
